import { Form } from "antd";
import { useNotification, useTranslate } from "@refinedev/core";
import { isAspectRatio, PlaybookFormValues } from "../types";
import { useEffect, useState } from "react";
import {
  SavedSettingsResponse,
  SavedSettingsUpdateRequest,
} from "pages/media/types/";
import {
  BOOLEAN_FIELDS_MEDIA_SETTINGS,
  BOOLEAN_FIELDS_STORYBOARD_SETTINGS,
} from "../config/fieldsConfig";
import {
  booleanToSelectValue,
  buildBooleanFieldValues,
  buildBooleanFieldValuesForSave,
  selectValueToBoolean,
  selectValueToTransitions,
  transitionsToSelectValue,
} from "../helpers";
import dayjs from "dayjs";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics/";

export const DEFAULT_DURATION_SECONDS = 60;

// Visual-media sources. A playbook with all of these disabled feeds the
// generation pipeline no images or video, so the project has nothing to render
// and fails. At least one must stay enabled — enforced on save below. Ordered
// as they appear in the form, so [0] is the field the UI scrolls to on error.
export const VISUAL_SOURCE_FIELDS = [
  "generate_images_for_slides",
  "image_extraction_enabled",
  "image_sourcing_enabled",
  "video_sourcing_enabled",
] as const satisfies readonly (keyof PlaybookFormValues)[];

// The visual-source toggles sit in a lazily-rendered Collapse panel; wait for
// the reveal callback's expand to render (and mount the fields) before
// scrolling the first one into view.
const REVEAL_SCROLL_DELAY_MS = 300;

export const usePlaybookForm = ({
  playbook,
  isAdmin = false,
  defaultBrandKitId,
  onSave,
  onVisualSourceMissing,
}: {
  playbook: SavedSettingsResponse;
  isAdmin?: boolean;
  defaultBrandKitId?: string | null;
  onSave: (values: SavedSettingsUpdateRequest) => Promise<void>;
  // Called when the save is blocked because no visual source is enabled, so
  // the UI can surface the offending toggles (expand + scroll to them).
  onVisualSourceMissing?: () => void;
}) => {
  const [form] = Form.useForm<PlaybookFormValues>();
  const [hasChanges, setHasChanges] = useState(false);

  const t = useTranslate();
  const { open } = useNotification();
  const { trackEvent } = useAnalytics();

  const savedSettings = playbook;
  const storyboardSettings = savedSettings.storyboard_settings;
  const mediaSettings = savedSettings.media_settings;
  // Track user modifications to duration separately from loaded data
  const [durationOverride, setDurationOverride] = useState<number | null>(null);
  const [durationToggleOverride, setDurationToggleOverride] = useState<
    boolean | null
  >(null);
  // Derive duration: use override if user modified, otherwise use loaded data
  const localDuration =
    durationOverride ??
    storyboardSettings?.duration ??
    DEFAULT_DURATION_SECONDS;
  const durationEnabled =
    durationToggleOverride ?? storyboardSettings?.duration != null;

  // Populate the form from the playbook, applying defaults for any unset
  // field. Runs unconditionally: a freshly created playbook has no
  // media/storyboard settings yet, but still needs its defaults seeded.
  useEffect(() => {
    const mediaBooleanFieldValues = buildBooleanFieldValues(
      BOOLEAN_FIELDS_MEDIA_SETTINGS,
      mediaSettings
    );
    const storyboardBooleanFieldValues = buildBooleanFieldValues(
      BOOLEAN_FIELDS_STORYBOARD_SETTINGS,
      storyboardSettings
    );

    const storedAspectRatio = mediaSettings?.aspect_ratio;
    form.setFieldsValue({
      name: savedSettings.name,
      description: savedSettings.description,
      language: storyboardSettings?.language,
      contact_id: mediaSettings?.contact_id,
      brand_kit_id: mediaSettings?.brand_kit_id ?? defaultBrandKitId ?? null,
      // Default to "mobile" (Portrait) when no valid ratio is stored.
      aspect_ratio: isAspectRatio(storedAspectRatio)
        ? storedAspectRatio
        : "mobile",
      theme_id: mediaSettings?.theme_id,
      objectives: storyboardSettings?.objectives ?? undefined,
      brief: storyboardSettings?.brief,
      image_style_description: storyboardSettings?.image_style_description,
      script_template_id: storyboardSettings?.script_template_id,
      highlights_template_id: storyboardSettings?.highlights_template_id,
      selected_pages: storyboardSettings?.selected_pages,
      preserve_original_doc: storyboardSettings?.preserve_original_doc ?? false,
      generation_date: storyboardSettings?.generation_date
        ? dayjs(storyboardSettings.generation_date)
        : null,
      storyboard_generation_mode:
        storyboardSettings?.storyboard_generation_mode ?? "Standard",
      media_generation_mode: mediaSettings?.media_generation_mode ?? "Video",
      chat_mode: mediaSettings?.chat_mode ?? "FullChat",
      expiration_time: mediaSettings?.expiration_time
        ? dayjs(mediaSettings.expiration_time)
        : null,
      music_asset_id: mediaSettings?.music_asset_id,
      cta_group_id: mediaSettings?.cta_group_id,
      instance_slug: mediaSettings?.instance_slug,
      transitions: transitionsToSelectValue(mediaSettings?.transitions),
      image_extraction_enabled:
        mediaSettings?.image_extraction_enabled ?? false,
      auto_select_music: booleanToSelectValue(mediaSettings?.auto_select_music),
      ...mediaBooleanFieldValues,
      ...storyboardBooleanFieldValues,
    });
  }, [
    savedSettings,
    storyboardSettings,
    mediaSettings,
    defaultBrandKitId,
    form,
  ]);

  const handleSave = async () => {
    try {
      // Surfaces inline errors (e.g. a required password) and blocks the save.
      await form.validateFields();
    } catch (validationError) {
      // Scroll to the first invalid field so the error isn't missed off-screen.
      const firstInvalidField = (
        validationError as { errorFields?: { name: (string | number)[] }[] }
      ).errorFields?.[0]?.name;
      if (firstInvalidField) {
        form.scrollToField(firstInvalidField, {
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    const formValues = form.getFieldsValue();

    // Every visual source is off → the project would generate no media and
    // fail, so block the save and tell the user to enable at least one.
    const hasVisualSource = VISUAL_SOURCE_FIELDS.some(
      (field) => formValues[field]
    );
    if (!hasVisualSource) {
      open?.({
        type: "error",
        message: t(
          "pages.playbooks.sections.advanced.visualSourceRequiredTitle"
        ),
        description: t("pages.playbooks.sections.advanced.visualSourceRequired"),
      });
      // Reveal the (possibly collapsed) panel, then scroll its first toggle
      // into view once it has mounted — same UX as the invalid-field scroll above.
      onVisualSourceMissing?.();
      globalThis.setTimeout(() => {
        form.scrollToField(VISUAL_SOURCE_FIELDS[0], {
          behavior: "smooth",
          block: "center",
        });
      }, REVEAL_SCROLL_DELAY_MS);
      return;
    }

    // Helper reads only the listed keys, so passing all form values is safe.
    const allFormValues = formValues as Record<string, boolean | undefined>;
    const mediaBooleanFieldValues = buildBooleanFieldValuesForSave(
      BOOLEAN_FIELDS_MEDIA_SETTINGS,
      allFormValues
    );
    const storyboardBooleanFieldValues = buildBooleanFieldValuesForSave(
      BOOLEAN_FIELDS_STORYBOARD_SETTINGS,
      allFormValues
    );

    await onSave({
      name: formValues.name,
      description: formValues.description,
      storyboard_settings: {
        language: formValues.language ?? null,
        duration: durationEnabled ? localDuration : null,
        objectives: formValues.objectives ?? null,
        brief: formValues.brief,
        image_style_description: formValues.image_style_description,
        ...storyboardBooleanFieldValues,
        ...(isAdmin && {
          script_template_id: formValues.script_template_id || null,
          highlights_template_id: formValues.highlights_template_id || null,
          selected_pages: formValues.selected_pages || null,
          generation_date: formValues.generation_date
            ? formValues.generation_date.format("YYYY-MM-DD")
            : null,
          preserve_original_doc: formValues.preserve_original_doc ?? null,
          storyboard_generation_mode:
            formValues.storyboard_generation_mode || null,
        }),
      },
      media_settings: {
        contact_id: formValues.contact_id ?? null,
        brand_kit_id: formValues.brand_kit_id ?? null,
        aspect_ratio: formValues.aspect_ratio ?? null,
        theme_id: formValues.theme_id ?? null,
        transitions: selectValueToTransitions(formValues.transitions),
        // Publication mode (and its password) are hidden for now — always
        // saved as null until the setting is re-enabled.
        publication_mode: null,
        password: null,
        image_extraction_enabled: formValues.image_extraction_enabled ?? false,
        media_generation_mode: formValues.media_generation_mode || null,
        chat_mode: formValues.chat_mode || null,
        ...(isAdmin && {
          expiration_time: formValues.expiration_time
            ? formValues.expiration_time.format("YYYY-MM-DD")
            : null,
          // Music asset only applies while auto-select is "enabled"; otherwise
          // it's cleared so a stale asset isn't kept against a non-enabled mode.
          auto_select_music: selectValueToBoolean(formValues.auto_select_music),
          music_asset_id:
            formValues.auto_select_music === "enabled"
              ? formValues.music_asset_id || null
              : null,
          cta_group_id: formValues.cta_group_id || null,
          instance_slug: formValues.instance_slug || null,
        }),
        ...mediaBooleanFieldValues,
      },
    });

    trackEvent(AnalyticsEvents.SettingsPlaybookUpdate, {
      playbook_id: playbook.id,
    });

    setHasChanges(false);
  };

  return {
    form,
    hasChanges,
    setHasChanges,
    saveSettings: handleSave,
    localDuration,
    durationEnabled,
    setDurationOverride,
    setDurationToggleOverride,
  };
};
